import { AdapterState } from '@tronweb3/tronwallet-abstract-adapter';
import { useWallet as useTronWallet } from '@tronweb3/tronwallet-adapter-react-hooks';
import { ForwardRefExoticComponent, SVGProps } from 'react';
import {
  useAccount,
  useDisconnect,
  useConnect as useWagmiConnect,
} from 'wagmi';
import MingcuteWalletLine from '~icons/mingcute/wallet-line';

export type Chain = 'evm' | 'tron' | 'd9';

export interface Connector {
  id: string;
  name: string;
  icon: { url: string } | ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  chain: Chain;
  isConnected: boolean;
}

export function useConnectors() {
  const { connectors: wagmiConnectors, isConnecting: wagmiIsConnecting } =
    useWagmiConnectors();
  const { connectors: tronConnectors, isConnecting: tronIsConnecting } =
    useTronConnectors();
  const { connectors: d9Connectors, isConnecting: d9IsConnecting } =
    useD9Connectors();

  return {
    connectors: useMemo<Connector[]>(() => {
      return [...wagmiConnectors, ...tronConnectors, ...d9Connectors];
    }, [wagmiConnectors, tronConnectors, d9Connectors]),
    isConnecting: useMemo(
      () => wagmiIsConnecting || tronIsConnecting || d9IsConnecting,
      [wagmiIsConnecting, tronIsConnecting, d9IsConnecting],
    ),
    connectedChains: useMemo<Chain[]>(() => {
      const chains = Array.from(
        new Set([
          ...wagmiConnectors
            .filter((connector) => connector.isConnected)
            .map((connector) => connector.chain),
          ...tronConnectors
            .filter((connector) => connector.isConnected)
            .map((connector) => connector.chain),
          ...d9Connectors
            .filter((connector) => connector.isConnected)
            .map((connector) => connector.chain),
        ]),
      );
      if (d9Connectors.find((c) => c.isConnected)) {
        chains.reverse();
      }
      return chains;
    }, [wagmiConnectors, tronConnectors, d9Connectors]),
  };
}

function useD9Connectors() {
  const d9Address = useD9Address();

  return {
    connectors: useMemo<Connector[]>(() => {
      if (!d9Address) return [];
      return [
        {
          id: 'd9',
          name: 'D9 App',
          icon: { url: '/images/icon/d9.png' },
          connect: async () => {},
          disconnect: async () => {},
          chain: 'd9',
          isConnected: true,
        } satisfies Connector,
      ];
    }, [d9Address]),
    isConnecting: useMemo(() => false, []),
  };
}

function useWagmiConnectors() {
  const { connectors: wagmiConnectors, status: wagmiStatus } =
    useWagmiConnect();
  const { isConnecting: wagmiIsConnecting, connector: connectedConnector } =
    useAccount();
  const { disconnectAsync: disconnectWagmiAsync } = useDisconnect();

  return {
    connectors: useMemo<Connector[]>(() => {
      const convertedWagmiConnectors = wagmiConnectors.map<Connector>(
        (wagmiConnector) => {
          const isConnected = connectedConnector?.id === wagmiConnector.id;
          let icon = MingcuteWalletLine;
          return {
            id: wagmiConnector.id,
            name: wagmiConnector.name,
            icon: !wagmiConnector.icon ? icon : { url: wagmiConnector.icon },
            chain: 'evm',
            isConnected,
            connect: async () => {
              await wagmiConnector.connect();
            },
            disconnect: async () => {
              if (isConnected) {
                await disconnectWagmiAsync();
              }
            },
          };
        },
      );

      return [...convertedWagmiConnectors];
    }, [wagmiConnectors, connectedConnector, disconnectWagmiAsync]),
    isConnecting: useMemo(
      () => wagmiIsConnecting || wagmiStatus === 'pending',
      [wagmiIsConnecting, wagmiStatus],
    ),
  };
}

function useTronConnectors() {
  const { connecting, wallets, disconnect } = useTronWallet();
  const anyOtherConnected = useMemo(
    () =>
      wallets.some(
        ({ adapter, state }) =>
          state === AdapterState.Connected && adapter.name !== 'TronLink',
      ),
    [wallets],
  );

  return {
    connectors: useMemo(
      () =>
        wallets
          .filter(({ adapter, state }) => {
            if (state === AdapterState.NotFound) return false;
            if (adapter.name === 'TronLink' && anyOtherConnected) return false;
            return true;
          })
          .map<Connector>(({ adapter, state }) => ({
            id: `tron-${adapter.name}`,
            name: adapter.name,
            isConnected: state === AdapterState.Connected,
            chain: 'tron',
            icon: { url: adapter.icon },
            connect: async () => {
              await adapter.connect();
            },
            disconnect: async () => {
              await disconnect();
            },
          })),
      [wallets, anyOtherConnected, disconnect],
    ),
    isConnecting: connecting,
  };
}
